import React, { useState, useEffect } from 'react';
import './Auth.css';
import './TicketAuth.css';
import 'boxicons/css/boxicons.min.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../Constant/Auth';
import { consumeExpiredReason } from '../Constant/authSession';
import LandingHeader from '../LandingPageLayout/LandingHeader';
import Footer from '../LandingPageLayout/Footer';
import Swal from 'sweetalert2';
import SearchableSelect from '../shared/SearchableSelect';

import {
  sendMobileOtp,
  verifyMobileOtp,
  loginUser,
  registerUser,
  sendPasswordResetOtp,
  verifyEmailOtp,
  resetPassword,
  getRegisterLibraries,
  getErrorMessage
} from '../api/api';

const initialRegisterData = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  mobile_no: '',
  role_id: 5,
  library_id: '',
  member_source: 'outsider',
  aadhaar_img: null,
  user_img: null
};

const AuthPage = () => {
  const [isRegister, setIsRegister] = useState(false);

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState(initialRegisterData);

  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const [libraries, setLibraries] = useState([]);
  const [error, setError] = useState('');
  const [sessionExpiredBanner, setSessionExpiredBanner] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Shown once, right here on the login page, when a previous session ended
  // because the 48h absolute session (or an unrecoverable refresh) expired —
  // never as a popup on every failed API call. The flag is sessionStorage-
  // backed and one-shot (see Constant/authSession.js), so it won't reappear
  // on a later visit to this page in the same tab.
  useEffect(() => {
    if (consumeExpiredReason()) {
      setSessionExpiredBanner(true);
    }
  }, []);

  useEffect(() => {
    if (location.state?.showRegister) {
      setIsRegister(true);
    } else if (location.state?.showRegister === false) {
      setIsRegister(false);
    }
  }, [location.state]);

  useEffect(() => {
    fetchLibraries();
  }, []);

  const fetchLibraries = async () => {
    try {
      const data = await getRegisterLibraries();
      setLibraries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fetch Libraries Error:', error);
    }
  };

  const resetRegisterForm = () => {
    setRegisterData(initialRegisterData);
    setOtp('');
    setOtpSent(false);
    setOtpVerified(false);
  };

  const toggleForm = () => {
    setIsRegister(!isRegister);
    setError('');
  };

  const handleForgotPassword = async () => {
    const { value: email } = await Swal.fire({
      title: 'Forgot Password',
      input: 'email',
      inputLabel: 'Enter your registered email address',
      inputPlaceholder: 'Email',
      confirmButtonText: 'Send OTP',
      showCancelButton: true,
      confirmButtonColor: '#7494ec'
    });

    if (!email) return;

    try {
      const sendRes = await sendPasswordResetOtp(email);

      if (!sendRes?.message) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Send OTP',
          text: sendRes?.message || 'Could not send OTP. Please try again.',
          confirmButtonColor: '#7494ec'
        });
        return;
      }

      const { value: emailOtp } = await Swal.fire({
        title: 'Verify OTP',
        input: 'text',
        inputLabel: 'Enter OTP sent to your email',
        inputPlaceholder: 'OTP',
        confirmButtonText: 'Verify OTP',
        showCancelButton: true,
        confirmButtonColor: '#7494ec',
        preConfirm: (value) => {
          if (!value) {
            Swal.showValidationMessage('Please enter the OTP');
            return false;
          }

          return value;
        }
      });

      if (!emailOtp) return;

      const verifyRes = await verifyEmailOtp(email, emailOtp);

      if (!verifyRes?.success) {
        Swal.fire({
          icon: 'error',
          title: 'OTP Verification Failed',
          text: verifyRes?.message || 'Invalid OTP. Please try again.',
          confirmButtonColor: '#7494ec'
        });
        return;
      }

      const { value: formValues } = await Swal.fire({
        title: 'Reset Password',
        html:
          `<input id="swal-input1" class="swal2-input" type="password" placeholder="New Password">` +
          `<input id="swal-input2" class="swal2-input" type="password" placeholder="Confirm New Password">`,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Reset Password',
        confirmButtonColor: '#7494ec',
        preConfirm: () => {
          const newPassword = document.getElementById('swal-input1').value;
          const confirmPassword = document.getElementById('swal-input2').value;

          if (!newPassword || !confirmPassword) {
            Swal.showValidationMessage('Please enter both fields');
            return false;
          }

          if (newPassword !== confirmPassword) {
            Swal.showValidationMessage('Passwords do not match');
            return false;
          }

          return {
            newPassword,
            confirmPassword
          };
        }
      });

      if (!formValues) return;

      const resetRes = await resetPassword(email, formValues.newPassword);

      const isSuccess =
        resetRes?.success ||
        resetRes?.message?.toLowerCase()?.includes('success');

      if (isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Password Reset!',
          text: resetRes?.message || 'Your password has been reset successfully.',
          confirmButtonColor: '#7494ec'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Reset Failed',
          text: resetRes?.message || 'Could not reset password.',
          confirmButtonColor: '#7494ec'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: getErrorMessage(error),
        confirmButtonColor: '#7494ec'
      });
    }
  };

  const sendOtp = async () => {
    if (!registerData.mobile_no) {
      Swal.fire({
        icon: 'error',
        title: 'Mobile number missing',
        text: 'Please enter mobile number first!',
        confirmButtonColor: '#7494ec'
      });
      return;
    }

    try {
      const res = await sendMobileOtp(registerData.mobile_no);

      if (res?.success) {
        setOtpSent(true);

        Swal.fire({
          icon: 'success',
          title: 'OTP Sent',
          text: res.message || 'OTP has been sent to your mobile number.',
          confirmButtonColor: '#7494ec'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Send OTP',
          text: res?.message || 'Please try again later.',
          confirmButtonColor: '#7494ec'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Send OTP',
        text: getErrorMessage(error),
        confirmButtonColor: '#7494ec'
      });
    }
  };

  const verifyOtp = async () => {
    if (!otp) {
      Swal.fire({
        icon: 'error',
        title: 'OTP Missing',
        text: 'Please enter the OTP.',
        confirmButtonColor: '#7494ec'
      });
      return;
    }

    try {
      const res = await verifyMobileOtp(registerData.mobile_no, otp);

      if (res?.success) {
        setOtpVerified(true);

        Swal.fire({
          icon: 'success',
          title: 'OTP Verified',
          text: res.message || 'Mobile number verified successfully.',
          confirmButtonColor: '#7494ec'
        });
      } else {
        setOtpVerified(false);

        Swal.fire({
          icon: 'error',
          title: 'Invalid OTP',
          text: res?.message || 'Verification failed',
          confirmButtonColor: '#7494ec'
        });
      }
    } catch (error) {
      setOtpVerified(false);

      Swal.fire({
        icon: 'error',
        title: 'Failed to verify OTP',
        text: getErrorMessage(error),
        confirmButtonColor: '#7494ec'
      });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await loginUser({
        email: loginData.email,
        password: loginData.password
      });

      if (response?.message === 'Login successful') {
        const user = response.user;
        const accessToken = response.accessToken;

        login(user, accessToken, response.sessionExpiresAt);

        switch (parseInt(user.role_id, 10)) {
          case 1:
            navigate('/admin/dashboard');
            break;

          case 2:
            navigate('/school/dashboard');
            break;

          case 3:
            navigate('/librarian/dashboard');
            break;

          case 4:
            navigate('/dashboard');
            break;

          case 5:
            navigate('/parent-dashboard');
            break;

          default:
             navigate('/parent-dashboard');
            navigate('/dashboard');
            break;
        }
      } else {
        setError(response?.error || response?.message || 'Login failed');
      }
    } catch (err) {
      console.error('Login Error:', err);
      const msg = err?.message || '';
      if (
        msg === 'Network Error' ||
        msg.toLowerCase().includes('timeout') ||
        msg.toLowerCase().includes('network')
      ) {
        setError('Server is not responding. Please check your connection and try again.');
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!otpVerified) {
      Swal.fire({
        icon: 'error',
        title: 'Verify Mobile Number',
        text: 'Please verify your mobile number using OTP before registration.',
        confirmButtonColor: '#7494ec'
      });
      return;
    }

    if (!registerData.library_id) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Library',
        text: 'Please select library.',
        confirmButtonColor: '#7494ec'
      });
      return;
    }

    if (!registerData.member_source) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Member Type',
        text: 'Please select member type.',
        confirmButtonColor: '#7494ec'
      });
      return;
    }

    if (!registerData.aadhaar_img) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Aadhaar Image',
        text: 'Please select an Aadhaar card image to upload.',
        confirmButtonColor: '#7494ec'
      });
      return;
    }

    if (!registerData.user_img) {
      Swal.fire({
        icon: 'error',
        title: 'Missing User Image',
        text: 'Please select user image to upload.',
        confirmButtonColor: '#7494ec'
      });
      return;
    }

    const formData = new FormData();

    formData.append('first_name', registerData.first_name);
    formData.append('last_name', registerData.last_name);
    formData.append('email', registerData.email);
    formData.append('password', registerData.password);
    formData.append('mobile_no', registerData.mobile_no);
    formData.append('role_id', registerData.role_id);
    formData.append('library_id', registerData.library_id);
    formData.append('member_source', registerData.member_source);
    formData.append('aadhaar_img', registerData.aadhaar_img);
    formData.append('user_img', registerData.user_img);

    try {
      const res = await registerUser(formData);

      const success =
        res?.success ||
        res?.message?.toLowerCase()?.includes('registered successfully');

      if (success) {
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful!',
          text: 'You can now log in.',
          confirmButtonColor: '#7494ec'
        });

        setIsRegister(false);
        resetRegisterForm();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: res?.message || 'Please try again.',
          confirmButtonColor: '#7494ec'
        });
      }
    } catch (err) {
      console.error('Registration Error:', err);

      Swal.fire({
        icon: 'error',
        title: 'Registration Failed',
        text: getErrorMessage(err),
        confirmButtonColor: '#7494ec'
      });
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ===== Ticketing-style auth page (login + register toggle) =====
  return (
    <>
      <LandingHeader showFeatures={false} />

      <div className={`tk-auth ${isRegister ? 'is-register' : ''}`}>
        <div className="tk-auth__hero">
          {/* <div className="tk-brand">
            <span className="tk-brand__mark">
               <img
                src="https://jaipuria.edu.in/wp-content/uploads/2021/07/sajes_great_place_logo.jpg"
                alt="Logo"
              /> 
            </span>
            <span className="tk-brand__name">
              Community Library
              <br />
              <small>MANAGEMENT SYSTEM</small>
            </span>
          </div> */}

          <div className="tk-hero__body">
            {/* <span className="tk-pill"><i></i> Now live — v2.0</span> */}
            <br/><br/><br/><br/>
            <h1 className="tk-hero__title">
              Welcome to Jaipuria Community Library Center!
             

             
            </h1>
           
          </div>

          {/* <div className="tk-hero__foot">© {new Date().getFullYear()} Jaipuria Community Library</div> */}
        </div>

        <div className="tk-auth__panel">
          <div className="tk-form">
            <span className="tk-eyebrow">{isRegister ? 'Get started' : 'Welcome back'}</span>
            <h2 className="tk-title">{isRegister ? 'Create your account' : 'Sign in to your account'}</h2>
            <p className="tk-subtitle">
              {isRegister ? 'Register to join the community library' : 'Login to continue to your library'}
            </p>

            <div className="tk-tabs">
              <button
                type="button"
                className={`tk-tab ${!isRegister ? 'is-active' : ''}`}
                onClick={() => { setIsRegister(false); setError(''); }}
              >
                Login
              </button>
              <button
                type="button"
                className={`tk-tab ${isRegister ? 'is-active' : ''}`}
                onClick={() => { setIsRegister(true); setError(''); }}
              >
                Register
              </button>
            </div>

            {sessionExpiredBanner && !isRegister && (
              <div
                className="tk-error"
                role="status"
                style={{ background: '#fff7e6', borderColor: '#f5c15c', color: '#8a5a00' }}
              >
                Your session has expired. Please log in again.
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => setSessionExpiredBanner(false)}
                  style={{
                    background: 'none',
                    border: 0,
                    color: 'inherit',
                    float: 'right',
                    cursor: 'pointer',
                    fontWeight: 700
                  }}
                >
                  ×
                </button>
              </div>
            )}

            {error && <p className="tk-error">{error}</p>}

            {!isRegister ? (
            <form onSubmit={handleLogin}>
              <label className="tk-label">Username or Email</label>
              <div className="tk-field">
                <i className="bx bxs-user"></i>
                <input
                  type="email"
                  placeholder="Enter username or email"
                  required
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                />
              </div>

              <label className="tk-label">Password</label>
              <div className="tk-field">
                <i className="bx bxs-lock-alt"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  required
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                />
                <button type="button" className="tk-eye" onClick={() => setShowPassword((s) => !s)}>
                  <i className={showPassword ? 'bx bx-hide' : 'bx bx-show'}></i>
                </button>
              </div>

              <div className="tk-row">
                <span
                  className="tk-link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleForgotPassword();
                  }}
                >
                  Forgot password?
                </span>
              </div>

              <button type="submit" className="tk-btn" disabled={isLoading}>
                {isLoading ? 'Signing in…' : <>Sign In <span>&#8594;</span></>}
              </button>
            </form>
            ) : (
            <form onSubmit={handleRegister} className="tk-register">

            <div className="row">
              <div className="col-md-6">
                <div className="input-box">
                  <input
                    type="text"
                    placeholder="First Name"
                    name="first_name"
                    value={registerData.first_name}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        first_name: e.target.value
                      })
                    }
                    required
                  />
                  <i className="bx bxs-user"></i>
                </div>
              </div>

              <div className="col-md-6">
                <div className="input-box">
                  <input
                    type="text"
                    placeholder="Last Name"
                    name="last_name"
                    value={registerData.last_name}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        last_name: e.target.value
                      })
                    }
                    required
                  />
                  <i className="bx bxs-user"></i>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="input-box">
                  <input
                    type="email"
                    placeholder="Email"
                    name="email"
                    value={registerData.email}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        email: e.target.value
                      })
                    }
                    required
                  />
                  <i className="bx bxs-envelope"></i>
                </div>
              </div>

              <div className="col-md-6">
                <div className="input-box">
                  <input
                    type="password"
                    placeholder="Password"
                    name="password"
                    value={registerData.password}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        password: e.target.value
                      })
                    }
                    required
                  />
                  <i className="bx bxs-lock-alt"></i>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="input-box">
                  <input
                    type="number"
                    placeholder="Mobile Number"
                    name="mobile_no"
                    value={registerData.mobile_no}
                    onChange={(e) => {
                      setRegisterData({
                        ...registerData,
                        mobile_no: e.target.value
                      });

                      setOtpSent(false);
                      setOtpVerified(false);
                      setOtp('');
                    }}
                    required
                  />
                  <i className="bx bxs-phone"></i>
                </div>
              </div>
            </div>

            <div className="row">
              {!otpSent ? (
                <button
                  type="button"
                  className="btn sent-btn"
                  onClick={sendOtp}
                  style={{ marginBottom: '10px' }}
                >
                  Send OTP
                </button>
              ) : (
                <>
                  <div className="input-box">
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                    <i className="bx bx-key"></i>
                  </div>

                  {!otpVerified && (
                    <button
                      type="button"
                      className="btn"
                      onClick={verifyOtp}
                      style={{ marginBottom: '10px' }}
                    >
                      Verify OTP
                    </button>
                  )}

                  {otpVerified && (
                    <p style={{ color: 'green', marginBottom: 10 }}>
                      Mobile number verified
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="input-box">
                  <SearchableSelect
                    required
                    clearable={false}
                    value={registerData.library_id}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        library_id: e.target.value
                      })
                    }
                    options={libraries.map((lib) => ({
                      value: lib.id,
                      label: lib.library_name || lib.name
                    }))}
                    placeholder="Select Library"
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="input-box">
                  <SearchableSelect
                    required
                    clearable={false}
                    value={registerData.member_source}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        member_source: e.target.value
                      })
                    }
                    options={[
                      { value: 'outsider', label: 'Outsider / Nearby Resident' },
                      { value: 'school_parent', label: 'School Parent' }
                    ]}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="input-box">
                  <label
                    htmlFor="aadhaar-upload"
                    className="custom-file-label"
                    style={{ textAlign: 'left' }}
                  >
                    {registerData.aadhaar_img
                      ? registerData.aadhaar_img.name
                      : 'Aadhaar Card Image'}
                  </label>

                  <input
                    id="aadhaar-upload"
                    type="file"
                    accept="image/*"
                    className="adharimg"
                    name="aadhaar_img"
                    onChange={(e) => {
                      const file = e.target.files[0];

                      setRegisterData({
                        ...registerData,
                        aadhaar_img: file
                      });
                    }}
                    required
                  />

                  <i className="bx bx-id-card adharicon"></i>
                </div>
              </div>

              <div className="col-md-6">
                <div className="input-box">
                  <label
                    htmlFor="user-upload"
                    className="custom-file-label"
                    style={{ textAlign: 'left' }}
                  >
                    {registerData.user_img
                      ? registerData.user_img.name
                      : 'User Image'}
                  </label>

                  <input
                    id="user-upload"
                    type="file"
                    accept="image/*"
                    className="adharimg"
                    name="user_img"
                    onChange={(e) => {
                      const file = e.target.files[0];

                      setRegisterData({
                        ...registerData,
                        user_img: file
                      });
                    }}
                    required
                  />

                  <i className="bx bx-id-card adharicon"></i>
                </div>
              </div>
            </div>

            <button type="submit" className="tk-btn">
              Register <span>&#8594;</span>
            </button>
          </form>
            )}

            <p className="tk-foot">
              {isRegister ? (
                <>
                  Already have an account?{' '}
                  <span className="tk-link" onClick={() => { setIsRegister(false); setError(''); }}>Login</span>
                </>
              ) : (
                <>
                  Don't have an account?{' '}
                  <span className="tk-link" onClick={() => { setIsRegister(true); setError(''); }}>Register</span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AuthPage;